// Palworld dedicated server settings (PalWorldSettings.ini).
// Defaults, ranges and descriptions for the first 90 settings are derived from the
// Palworld Dedicated Server Config Creator (https://github.com/BlinkZer0/Palworld-Dedicated-Server-Config-Creator),
// used under the MIT License below. The "Palworld 1.0 additions" come from Pocketpair's official
// server guide (docs.palworldgame.com, 1.0.4), which gives no defaults for them, so they're only written when set.
/*
MIT License

Copyright (c) 2025 Palworld Dedicated Server Config Creator Contributors

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/
import type { PalSettingsData } from "./pal-server-settings";

export const PAL_SETTINGS_DATA: PalSettingsData = {
 "order": [
  "Difficulty",
  "RandomizerType",
  "RandomizerSeed",
  "bIsRandomizerPalLevelRandom",
  "DayTimeSpeedRate",
  "NightTimeSpeedRate",
  "ExpRate",
  "PalCaptureRate",
  "PalSpawnNumRate",
  "PalDamageRateAttack",
  "PalDamageRateDefense",
  "PlayerDamageRateAttack",
  "PlayerDamageRateDefense",
  "PlayerStomachDecreaceRate",
  "PlayerStaminaDecreaceRate",
  "PlayerAutoHPRegeneRate",
  "PlayerAutoHpRegeneRateInSleep",
  "PalStomachDecreaceRate",
  "PalStaminaDecreaceRate",
  "PalAutoHPRegeneRate",
  "PalAutoHpRegeneRateInSleep",
  "BuildObjectHpRate",
  "BuildObjectDamageRate",
  "BuildObjectDeteriorationDamageRate",
  "CollectionDropRate",
  "CollectionObjectHpRate",
  "CollectionObjectRespawnSpeedRate",
  "EnemyDropItemRate",
  "DeathPenalty",
  "bEnablePlayerToPlayerDamage",
  "bEnableFriendlyFire",
  "bEnableInvaderEnemy",
  "bActiveUNKO",
  "bEnableAimAssistPad",
  "bEnableAimAssistKeyboard",
  "DropItemMaxNum",
  "DropItemMaxNum_UNKO",
  "BaseCampMaxNum",
  "BaseCampWorkerMaxNum",
  "DropItemAliveMaxHours",
  "bAutoResetGuildNoOnlinePlayers",
  "AutoResetGuildTimeNoOnlinePlayers",
  "GuildPlayerMaxNum",
  "BaseCampMaxNumInGuild",
  "PalEggDefaultHatchingTime",
  "WorkSpeedRate",
  "AutoSaveSpan",
  "bIsMultiplay",
  "bIsPvP",
  "bHardcore",
  "bPalLost",
  "bCharacterRecreateInHardcore",
  "bCanPickupOtherGuildDeathPenaltyDrop",
  "bEnableNonLoginPenalty",
  "bEnableFastTravel",
  "bIsStartLocationSelectByMap",
  "bExistPlayerAfterLogout",
  "bEnableDefenseOtherGuildPlayer",
  "bInvisibleOtherGuildBaseCampAreaFX",
  "bBuildAreaLimit",
  "ItemWeightRate",
  "CoopPlayerMaxNum",
  "ServerPlayerMaxNum",
  "ServerName",
  "ServerDescription",
  "AdminPassword",
  "ServerPassword",
  "PublicPort",
  "PublicIP",
  "RCONEnabled",
  "RCONPort",
  "Region",
  "bUseAuth",
  "BanListURL",
  "RESTAPIEnabled",
  "RESTAPIPort",
  "bShowPlayerList",
  "ChatPostLimitPerMinute",
  "CrossplayPlatforms",
  "bIsUseBackupSaveData",
  "LogFormatType",
  "SupplyDropSpan",
  "EnablePredatorBossPal",
  "MaxBuildingLimitNum",
  "ServerReplicatePawnCullDistance",
  "bAllowGlobalPalboxExport",
  "bAllowGlobalPalboxImport",
  "EquipmentDurabilityDamageRate",
  "ItemContainerForceMarkDirtyInterval",
  "ItemCorruptionMultiplier"
 ],
 "sections": [
  {
   "title": "Server Information",
   "settings": [
    {
     "key": "ServerName",
     "kind": "text",
     "label": "Server Name",
     "help": "Custom name displayed in the server browser. Players will see this when searching for servers to join.",
     "default": "Default Palworld Server"
    },
    {
     "key": "ServerDescription",
     "kind": "text",
     "label": "Server Description",
     "help": "Detailed description text about your server. Helps players understand your server's rules, playstyle, or special features.",
     "default": ""
    },
    {
     "key": "ServerPassword",
     "kind": "password",
     "label": "Server Password",
     "help": "Login password required to join the server. Leave empty for a public server. Use this to create a private server for friends.",
     "default": ""
    },
    {
     "key": "AdminPassword",
     "kind": "password",
     "label": "Admin Password",
     "help": "Administrative access credentials for server management. Required to use admin commands and perform server maintenance.",
     "default": ""
    },
    {
     "key": "PublicIP",
     "kind": "text",
     "label": "Public IP",
     "help": "Your server's external IP address for manual network configuration. Usually auto-detected. Only set if having connection issues.",
     "default": ""
    },
    {
     "key": "PublicPort",
     "kind": "number",
     "label": "Public Port",
     "help": "Port for game connections. Default is 8211. Must be forwarded in your router if hosting from home. Players connect using IP:Port.",
     "default": 8211.0,
     "min": 1024.0,
     "max": 65535.0,
     "step": 1.0
    },
    {
     "key": "Region",
     "kind": "text",
     "label": "Region",
     "help": "Server region identifier (e.g., NA, EU, ASIA). Helps players find servers in their geographic area for better latency.",
     "default": ""
    },
    {
     "key": "bUseAuth",
     "kind": "bool",
     "label": "Use Authentication",
     "help": "Enable platform authentication (Steam/Xbox/PlayStation). Prevents unauthorized access and helps with ban management.",
     "default": true
    },
    {
     "key": "bShowPlayerList",
     "kind": "bool",
     "label": "Show Player List",
     "help": "Display the list of online players publicly. When disabled, only admins can see who's online for privacy.",
     "default": false
    }
   ]
  },
  {
   "title": "Multiplayer Settings",
   "settings": [
    {
     "key": "ServerPlayerMaxNum",
     "kind": "number",
     "label": "Max Players",
     "help": "Maximum concurrent players allowed on the server (1-32). Official servers support up to 32 players. Higher values may impact performance.",
     "default": 32.0,
     "min": 1.0,
     "max": 32.0,
     "step": 1.0
    },
    {
     "key": "CoopPlayerMaxNum",
     "kind": "number",
     "label": "Co-op Players",
     "help": "Maximum party size for cooperative play (1-4). Controls how many players can join together in a co-op group. Note: This setting may not always function as expected.",
     "default": 4.0,
     "min": 1.0,
     "max": 4.0,
     "step": 1.0
    },
    {
     "key": "GuildPlayerMaxNum",
     "kind": "number",
     "label": "Guild Max Players",
     "help": "Maximum members allowed per guild/faction (1-100). Larger guilds enable more cooperative base building and PvP coordination.",
     "default": 20.0,
     "min": 1.0,
     "max": 100.0,
     "step": 1.0
    },
    {
     "key": "bIsMultiplay",
     "kind": "bool",
     "label": "Multiplayer Enabled",
     "help": "Enable multiplayer functionality. When disabled, server runs in single-player mode. Must be enabled for online play.",
     "default": false
    },
    {
     "key": "bIsPvP",
     "kind": "bool",
     "label": "PvP Enabled",
     "help": "Enable player-versus-player combat. When enabled, players can attack each other outside of safe zones. Affects overall server playstyle.",
     "default": false
    },
    {
     "key": "bEnablePlayerToPlayerDamage",
     "kind": "bool",
     "label": "Player-to-Player Damage",
     "help": "Allow players to damage each other directly. Works in conjunction with PvP settings. Disable for pure cooperative gameplay.",
     "default": false
    },
    {
     "key": "bEnableFriendlyFire",
     "kind": "bool",
     "label": "Friendly Fire",
     "help": "Enable damage to allies and guild members. When enabled, be careful with area attacks! Adds realism but can cause accidents.",
     "default": false
    },
    {
     "key": "bEnableDefenseOtherGuildPlayer",
     "kind": "bool",
     "label": "Defense vs Other Guilds",
     "help": "Enable defensive actions against players from other guilds. Allows protecting territory from rival factions.",
     "default": false
    },
    {
     "key": "bCanPickupOtherGuildDeathPenaltyDrop",
     "kind": "bool",
     "label": "Pickup Other Guild Drops",
     "help": "Allow picking up items dropped by players from other guilds when they die. Creates risk vs reward for PvP encounters.",
     "default": false
    },
    {
     "key": "CrossplayPlatforms",
     "kind": "platforms",
     "label": "Crossplay Platforms",
     "help": "Select which gaming platforms can connect to your server. Supports Steam, Xbox, PS5, and Mac. More platforms = larger player pool.",
     "default": [
      "Steam",
      "Xbox",
      "PS5",
      "Mac"
     ]
    }
   ]
  },
  {
   "title": "Gameplay Settings",
   "settings": [
    {
     "key": "Difficulty",
     "kind": "enum",
     "label": "Difficulty",
     "help": "Game difficulty preset. Note: Currently not fully functional. Use individual multipliers below for fine-tuning difficulty instead.",
     "options": [
      [
       "None",
       "None"
      ],
      [
       "Normal",
       "Normal"
      ],
      [
       "Difficult",
       "Difficult"
      ]
     ],
     "default": "None"
    },
    {
     "key": "DeathPenalty",
     "kind": "enum",
     "label": "Death Penalty",
     "help": "What you lose when you die. None = keep everything, Item = lose inventory items, ItemAndEquipment = lose items + worn gear, All = lose everything including Pals in party.",
     "options": [
      [
       "None",
       "None"
      ],
      [
       "Item",
       "Items Only"
      ],
      [
       "ItemAndEquipment",
       "Items & Equipment"
      ],
      [
       "All",
       "All"
      ]
     ],
     "default": "All"
    },
    {
     "key": "ExpRate",
     "kind": "number",
     "label": "Experience Rate",
     "help": "XP gain multiplier for both players and Pals (0.1-10.0). 1.0 = default, 2.0 = double XP, 0.5 = half XP. Higher values = faster leveling.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PalCaptureRate",
     "kind": "number",
     "label": "Pal Capture Rate",
     "help": "Pal capture probability multiplier (0.1-10.0). 1.0 = default catch rates, 2.0 = twice as easy, 0.5 = twice as hard. Affects sphere success chance.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "DayTimeSpeedRate",
     "kind": "number",
     "label": "Day Speed Rate",
     "help": "Day cycle speed multiplier (0.1-10.0). 1.0 = normal 30min days, 2.0 = 15min days (faster), 0.5 = 60min days (slower). Affects gameplay pacing.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "NightTimeSpeedRate",
     "kind": "number",
     "label": "Night Speed Rate",
     "help": "Night cycle speed multiplier (0.1-10.0). Works same as day speed. Set higher to shorten nights, lower to extend them. Separate from day rate for flexibility.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "bHardcore",
     "kind": "bool",
     "label": "Hardcore Mode",
     "help": "Enable permanent death mode. When your character dies, you must delete and recreate. Creates intense high-stakes gameplay. Backup your save!",
     "default": false
    },
    {
     "key": "bPalLost",
     "kind": "bool",
     "label": "Pal Lost on Death",
     "help": "Pals in your party are permanently lost when you die. Extremely punishing! Only for the most hardcore players. Pairs well with Hardcore Mode.",
     "default": false
    },
    {
     "key": "bCharacterRecreateInHardcore",
     "kind": "bool",
     "label": "Character Recreate in Hardcore",
     "help": "Allow creating a new character in Hardcore mode after death instead of being locked out. Provides second chances in perma-death gameplay.",
     "default": false
    },
    {
     "key": "bEnableFastTravel",
     "kind": "bool",
     "label": "Fast Travel Enabled",
     "help": "Allow teleporting between discovered fast travel points. Disable for more realistic travel or to increase world exploration difficulty.",
     "default": true
    },
    {
     "key": "bIsStartLocationSelectByMap",
     "kind": "bool",
     "label": "Select Start Location",
     "help": "Allow choosing your spawn point on the map when creating a character. When disabled, spawns are random, adding unpredictability.",
     "default": true
    },
    {
     "key": "bEnableAimAssistPad",
     "kind": "bool",
     "label": "Aim Assist (Gamepad)",
     "help": "Enable aim assistance for controller/gamepad users. Helps with aiming during combat on console or when using controllers on PC.",
     "default": true
    },
    {
     "key": "bEnableAimAssistKeyboard",
     "kind": "bool",
     "label": "Aim Assist (Keyboard)",
     "help": "Enable aim assistance for keyboard/mouse users. Generally not recommended as mouse aiming is already precise. Most players keep this off.",
     "default": false
    }
   ]
  },
  {
   "title": "Randomizer Settings",
   "settings": [
    {
     "key": "RandomizerType",
     "kind": "enum",
     "label": "Randomizer Type",
     "help": "World randomization mode. None = standard world, Region = randomize Pal spawns per region, All = complete randomization. Creates unique world experiences each playthrough.",
     "options": [
      [
       "None",
       "None"
      ],
      [
       "Region",
       "Region"
      ],
      [
       "All",
       "All"
      ]
     ],
     "default": "None"
    },
    {
     "key": "RandomizerSeed",
     "kind": "text",
     "label": "Randomizer Seed",
     "help": "Custom seed for randomization. Leave empty for truly random generation. Use same seed to recreate identical randomized worlds across servers.",
     "default": ""
    },
    {
     "key": "bIsRandomizerPalLevelRandom",
     "kind": "bool",
     "label": "Randomize Pal Levels",
     "help": "Randomize wild Pal levels instead of using zone-based scaling. Creates unpredictable encounters - low level areas may have high level Pals!",
     "default": false
    }
   ]
  },
  {
   "title": "Player Settings",
   "settings": [
    {
     "key": "PlayerDamageRateAttack",
     "kind": "number",
     "label": "Player Attack Damage",
     "help": "Player damage dealt multiplier (0.1-10.0). 1.0 = default damage, 2.0 = double damage, 0.5 = half damage. Makes combat easier/harder.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PlayerDamageRateDefense",
     "kind": "number",
     "label": "Player Defense",
     "help": "Player damage received multiplier (0.1-10.0). 1.0 = normal damage taken, 0.5 = take half damage (easier), 2.0 = take double damage (harder).",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PlayerStomachDecreaceRate",
     "kind": "number",
     "label": "Hunger Decrease Rate",
     "help": "How fast player hunger depletes (0.1-10.0). 1.0 = normal, 2.0 = get hungry twice as fast, 0.5 = slower hunger. Affects food consumption frequency.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PlayerStaminaDecreaceRate",
     "kind": "number",
     "label": "Stamina Decrease Rate",
     "help": "How fast stamina drains during activities (0.1-10.0). 1.0 = normal, 0.5 = stamina lasts longer, 2.0 = stamina drains faster. Affects sprinting, climbing, etc.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PlayerAutoHPRegeneRate",
     "kind": "number",
     "label": "HP Regen Rate",
     "help": "Player health regeneration speed (0.1-10.0). 1.0 = default regen, 2.0 = heal twice as fast, 0.5 = slower healing. Affects passive HP recovery.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PlayerAutoHpRegeneRateInSleep",
     "kind": "number",
     "label": "HP Regen (Sleep)",
     "help": "HP regeneration rate while sleeping in bed (0.1-10.0). Higher values = wake up healthier. Separate from active regen for balancing rest mechanics.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "ItemWeightRate",
     "kind": "number",
     "label": "Item Weight Rate",
     "help": "Item weight/encumbrance multiplier (0.1-10.0). 1.0 = normal weight, 0.5 = items weigh half (carry more), 2.0 = items weigh double (carry less).",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "bExistPlayerAfterLogout",
     "kind": "bool",
     "label": "Player Exists After Logout",
     "help": "Player body remains vulnerable in world after logout. Disable for safe logout. Enable for realistic survival where you can be killed while offline.",
     "default": false
    },
    {
     "key": "bEnableNonLoginPenalty",
     "kind": "bool",
     "label": "Non-Login Penalty",
     "help": "Enable penalties for inactive players. Bases may deteriorate or resources decay if not maintained. Keeps server active and prevents abandoned bases.",
     "default": true
    }
   ]
  },
  {
   "title": "Pal Settings",
   "settings": [
    {
     "key": "PalSpawnNumRate",
     "kind": "number",
     "label": "Pal Spawn Rate",
     "help": "Wild Pal spawn frequency multiplier (0.1-10.0). 1.0 = normal spawns, 2.0 = twice as many Pals, 0.5 = half as many. Affects world population density.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PalDamageRateAttack",
     "kind": "number",
     "label": "Pal Attack Damage",
     "help": "Pal damage dealt multiplier (0.1-10.0). Affects both your Pals and wild Pals. 1.0 = default, 2.0 = Pals hit twice as hard, 0.5 = half damage.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PalDamageRateDefense",
     "kind": "number",
     "label": "Pal Defense",
     "help": "Pal damage received multiplier (0.1-10.0). 1.0 = normal, 0.5 = Pals take half damage (tankier), 2.0 = Pals take double damage (die faster).",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PalStomachDecreaceRate",
     "kind": "number",
     "label": "Pal Hunger Decrease",
     "help": "How fast Pals get hungry (0.1-10.0). 1.0 = normal, 2.0 = need to feed twice as often, 0.5 = need less food. Affects base food consumption.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PalStaminaDecreaceRate",
     "kind": "number",
     "label": "Pal Stamina Decrease",
     "help": "Pal stamina drain rate (0.1-10.0). Affects riding, combat, and work. Lower = Pals can work/fight longer without rest.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PalAutoHPRegeneRate",
     "kind": "number",
     "label": "Pal HP Regen",
     "help": "Pal health regeneration speed (0.1-10.0). 1.0 = default healing, 2.0 = heal twice as fast, 0.5 = slower healing. Passive recovery rate.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PalAutoHpRegeneRateInSleep",
     "kind": "number",
     "label": "Pal HP Regen (Sleep)",
     "help": "HP regen while Pals are resting in Palbox (0.1-10.0). Higher = faster recovery when not deployed. Helps with base management.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "PalEggDefaultHatchingTime",
     "kind": "number",
     "label": "Pal Egg Hatch Time (hours)",
     "help": "Real-time hours for Pal eggs to hatch (0-240). Default is 72 hours (3 days). Lower for faster breeding, higher for more realistic incubation.",
     "default": 72.0,
     "min": 0.0,
     "max": 240.0,
     "step": 1.0
    },
    {
     "key": "bAllowGlobalPalboxExport",
     "kind": "bool",
     "label": "Allow Global Palbox Export",
     "help": "Allow moving Pals OUT of the global Palbox system. Disable to prevent transferring Pals between bases or players.",
     "default": true
    },
    {
     "key": "bAllowGlobalPalboxImport",
     "kind": "bool",
     "label": "Allow Global Palbox Import",
     "help": "Allow moving Pals INTO the global Palbox system. When disabled, Pals stay at their current base only.",
     "default": false
    },
    {
     "key": "EnablePredatorBossPal",
     "kind": "bool",
     "label": "Enable Predator Boss Pals",
     "help": "Enable special predator-type boss Pals to spawn in the world. These are rare, powerful encounters with unique rewards.",
     "default": true
    }
   ]
  },
  {
   "title": "Base & Building Settings",
   "settings": [
    {
     "key": "BaseCampMaxNum",
     "kind": "number",
     "label": "Max Base Camps",
     "help": "Maximum base camps per player (1-128). Allows building multiple bases across the map. More bases = more resource production but harder to defend.",
     "default": 128.0,
     "min": 1.0,
     "max": 128.0,
     "step": 1.0
    },
    {
     "key": "BaseCampWorkerMaxNum",
     "kind": "number",
     "label": "Max Workers per Base",
     "help": "Maximum Pals that can work at each base (1-50). Default is 15. More workers = faster production but requires more food. Note: May not always function as expected.",
     "default": 15.0,
     "min": 1.0,
     "max": 50.0,
     "step": 1.0
    },
    {
     "key": "BaseCampMaxNumInGuild",
     "kind": "number",
     "label": "Max Bases in Guild",
     "help": "Maximum base camps per guild/group (1-10). Guild members can share and work together at these bases. Enables large-scale cooperation.",
     "default": 4.0,
     "min": 1.0,
     "max": 10.0,
     "step": 1.0
    },
    {
     "key": "BuildObjectHpRate",
     "kind": "number",
     "label": "Building HP Rate",
     "help": "Building health multiplier (0.1-10.0). 1.0 = normal HP, 2.0 = double durability, 0.5 = half HP. Higher = structures harder to destroy in raids.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "BuildObjectDamageRate",
     "kind": "number",
     "label": "Building Damage Rate",
     "help": "Damage TO buildings multiplier (0.1-10.0). 1.0 = normal, 2.0 = buildings take double damage, 0.5 = buildings take half damage. Affects PvP raiding.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "BuildObjectDeteriorationDamageRate",
     "kind": "number",
     "label": "Building Deterioration",
     "help": "Passive decay rate for structures (0.1-10.0). 1.0 = normal decay, 0.0 = no decay, 2.0 = faster decay. Affects long-term base maintenance.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "WorkSpeedRate",
     "kind": "number",
     "label": "Work Speed Rate",
     "help": "Pal work performance speed (0.1-10.0). 1.0 = normal speed, 2.0 = work twice as fast, 0.5 = work half speed. Affects crafting, farming, mining at bases.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "MaxBuildingLimitNum",
     "kind": "number",
     "label": "Max Building Limit",
     "help": "Maximum number of structures per base (0-100000). 0 = unlimited. Set a limit to prevent lag from over-building or create building strategy challenges.",
     "default": 0.0,
     "min": 0.0,
     "max": 100000.0,
     "step": 1.0
    },
    {
     "key": "bBuildAreaLimit",
     "kind": "bool",
     "label": "Build Area Limit",
     "help": "Restrict building near dungeons, POIs, and other protected structures. Prevents exploits and maintains world integrity.",
     "default": false
    },
    {
     "key": "bInvisibleOtherGuildBaseCampAreaFX",
     "kind": "bool",
     "label": "Hide Other Guild Base FX",
     "help": "Hide visual effects of rival guild bases. Improves performance in areas with many nearby bases. Helps FPS on lower-end systems.",
     "default": false
    }
   ]
  },
  {
   "title": "Drops & Resources",
   "settings": [
    {
     "key": "CollectionDropRate",
     "kind": "number",
     "label": "Collection Drop Rate",
     "help": "Resource yield multiplier when gathering (0.1-10.0). 1.0 = normal drops, 2.0 = double resources, 0.5 = half resources. Affects trees, rocks, ore nodes.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "CollectionObjectHpRate",
     "kind": "number",
     "label": "Collection Object HP",
     "help": "Health of harvestable nodes (0.1-10.0). 1.0 = normal HP, 2.0 = nodes take twice as many hits, 0.5 = nodes break faster. Affects gathering speed.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "CollectionObjectRespawnSpeedRate",
     "kind": "number",
     "label": "Resource Respawn Speed",
     "help": "How fast resources respawn (0.1-10.0). 1.0 = normal respawn, 2.0 = respawn twice as fast, 0.5 = respawn twice as slow. Affects world resource availability.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "EnemyDropItemRate",
     "kind": "number",
     "label": "Enemy Drop Rate",
     "help": "Enemy/Pal item drop rate (0.1-10.0). 1.0 = normal loot, 2.0 = double drops, 0.5 = half drops. Affects resources gained from defeated enemies.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "DropItemMaxNum",
     "kind": "number",
     "label": "Max Dropped Items",
     "help": "Maximum item entities in world (100-10000). Higher = less cleanup but potential lag. Lower = better performance but items may despawn faster. Default: 3000.",
     "default": 3000.0,
     "min": 100.0,
     "max": 10000.0,
     "step": 1.0
    },
    {
     "key": "DropItemMaxNum_UNKO",
     "kind": "number",
     "label": "Max Dropped Items (UNKO)",
     "help": "Maximum special/poop items (10-1000). Separate limit for Pal waste products. Lower if experiencing lag from too many special drops.",
     "default": 100.0,
     "min": 10.0,
     "max": 1000.0,
     "step": 1.0
    },
    {
     "key": "DropItemAliveMaxHours",
     "kind": "number",
     "label": "Drop Item Lifetime (hours)",
     "help": "How long dropped items persist (0.1-24 hours). Default: 1 hour. Lower = cleaner world, higher = more time to retrieve items. Affects server cleanup.",
     "default": 1.0,
     "min": 0.1,
     "max": 24.0,
     "step": 0.1
    },
    {
     "key": "SupplyDropSpan",
     "kind": "number",
     "label": "Supply Drop Interval (minutes)",
     "help": "Time between supply drop events (10-600 minutes). Default: 180 (3 hours). Supply drops contain rare loot crates that spawn across the map.",
     "default": 180.0,
     "min": 10.0,
     "max": 600.0,
     "step": 1.0
    },
    {
     "key": "EquipmentDurabilityDamageRate",
     "kind": "number",
     "label": "Equipment Durability Damage",
     "help": "Equipment durability loss rate (0.1-10.0). 1.0 = normal wear, 2.0 = break twice as fast, 0.5 = last twice as long. Affects repair frequency.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "ItemCorruptionMultiplier",
     "kind": "number",
     "label": "Item Corruption Multiplier",
     "help": "Item corruption/decay rate (0.1-10.0). Affects how fast items degrade or spoil. Higher = faster corruption, lower = items last longer in inventory.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    }
   ]
  },
  {
   "title": "Guild & Auto-Reset Settings",
   "settings": [
    {
     "key": "bAutoResetGuildNoOnlinePlayers",
     "kind": "bool",
     "label": "Auto Reset Guild (No Online)",
     "help": "Automatically reset/disband guilds when all members haven't logged in for specified time. Cleans up abandoned guilds and their bases from the server.",
     "default": false
    },
    {
     "key": "AutoResetGuildTimeNoOnlinePlayers",
     "kind": "number",
     "label": "Auto Reset Time (hours)",
     "help": "Hours of inactivity before guild is auto-reset (1-168). Default: 72 hours (3 days). Guild is deleted and bases released if ALL members offline this long.",
     "default": 72.0,
     "min": 1.0,
     "max": 168.0,
     "step": 1.0
    }
   ]
  },
  {
   "title": "Enemy & Invader Settings",
   "settings": [
    {
     "key": "bEnableInvaderEnemy",
     "kind": "bool",
     "label": "Enable Invader Enemies",
     "help": "Enable enemy invasion/raid events at bases. When enabled, hostile Pals will periodically attack player bases. Disable for peaceful base building.",
     "default": true
    },
    {
     "key": "bActiveUNKO",
     "kind": "bool",
     "label": "Active UNKO",
     "help": "Enable special/poop drops from Pals. Pal waste products can be used as fertilizer or special resources. Disable to remove this mechanic entirely.",
     "default": false
    }
   ]
  },
  {
   "title": "Server Management",
   "settings": [
    {
     "key": "AutoSaveSpan",
     "kind": "number",
     "label": "Auto Save Interval (minutes)",
     "help": "Time between auto-saves (5-60 minutes). Default: 30. More frequent = safer but higher disk usage. Less frequent = better performance but more potential data loss.",
     "default": 30.0,
     "min": 5.0,
     "max": 60.0,
     "step": 1.0
    },
    {
     "key": "ChatPostLimitPerMinute",
     "kind": "number",
     "label": "Chat Post Limit/Min",
     "help": "Max chat messages per player per minute (1-100). Default: 30. Prevents chat spam. Lower = stricter anti-spam, higher = more freedom.",
     "default": 30.0,
     "min": 1.0,
     "max": 100.0,
     "step": 1.0
    },
    {
     "key": "BanListURL",
     "kind": "text",
     "label": "Ban List URL",
     "help": "URL to external ban list. Server will automatically ban players on this list. Official URL auto-bans known cheaters. Leave empty to disable.",
     "default": "https://api.palworldgame.com/api/banlist.txt"
    },
    {
     "key": "LogFormatType",
     "kind": "enum",
     "label": "Log Format Type",
     "help": "Server log file format. Text = human-readable logs, JSON = machine-readable for log analysis tools. Choose JSON if using log parsers.",
     "options": [
      [
       "Text",
       "Text"
      ],
      [
       "JSON",
       "JSON"
      ]
     ],
     "default": "Text"
    },
    {
     "key": "bIsUseBackupSaveData",
     "kind": "bool",
     "label": "Use Backup Save Data",
     "help": "Enable automatic save backups. Creates rolling backups: 5 every 30sec, 6 every 10min, 12 hourly, 7 daily. Critical for data protection!",
     "default": true
    },
    {
     "key": "ItemContainerForceMarkDirtyInterval",
     "kind": "number",
     "label": "Item Container Dirty Interval",
     "help": "Seconds between chest/container state updates (0.1-10). Higher = better performance but slower container sync. Lower = real-time updates but more server load.",
     "default": 1.0,
     "min": 0.1,
     "max": 10.0,
     "step": 0.1
    },
    {
     "key": "ServerReplicatePawnCullDistance",
     "kind": "number",
     "label": "Server Pawn Cull Distance",
     "help": "Distance in units before entities are hidden/culled (1000-50000). Lower = better performance, higher = see farther. Affects view distance of Pals/players.",
     "default": 15000.0,
     "min": 1000.0,
     "max": 50000.0,
     "step": 1000.0
    }
   ]
  },
  {
   "title": "RCON & REST API",
   "settings": [
    {
     "key": "RCONEnabled",
     "kind": "bool",
     "label": "Enable RCON",
     "help": "Enable Remote Console protocol for server administration. Allows sending commands remotely using RCON tools. Must be port forwarded if using remotely.",
     "default": false
    },
    {
     "key": "RCONPort",
     "kind": "number",
     "label": "RCON Port",
     "help": "RCON connection port (1024-65535). Default: 25575. Must differ from game port. Use for remote admin tools. Remember to forward this port!",
     "default": 25575.0,
     "min": 1024.0,
     "max": 65535.0,
     "step": 1.0
    },
    {
     "key": "RESTAPIEnabled",
     "kind": "bool",
     "label": "Enable REST API",
     "help": "Enable REST API for web-based server management and monitoring. Allows external tools/websites to query server status and send commands via HTTP.",
     "default": false
    },
    {
     "key": "RESTAPIPort",
     "kind": "number",
     "label": "REST API Port",
     "help": "REST API HTTP port (1024-65535). Default: 8212. Used by web dashboards and monitoring tools. Ensure firewall allows if using externally.",
     "default": 8212.0,
     "min": 1024.0,
     "max": 65535.0,
     "step": 1.0
    }
   ]
  },
  {
   "title": "Palworld 1.0 additions",
   "optional": true,
   "settings": [
    {
     "key": "bEnableVoiceChat",
     "kind": "bool",
     "label": "Voice chat",
     "help": "Enables in-game voice chat.",
     "default": null
    },
    {
     "key": "VoiceChatMaxVolumeDistance",
     "kind": "number",
     "label": "Voice full-volume distance",
     "help": "Distance within which voice volume doesn't drop.",
     "default": null
    },
    {
     "key": "VoiceChatZeroVolumeDistance",
     "kind": "number",
     "label": "Voice silent distance",
     "help": "Distance at which voice volume reaches zero.",
     "default": null
    },
    {
     "key": "bIsShowJoinLeftMessage",
     "kind": "bool",
     "label": "Join/leave messages",
     "help": "Shows player join and leave messages in game.",
     "default": null
    },
    {
     "key": "bAllowClientMod",
     "kind": "bool",
     "label": "Allow modded clients",
     "help": "Lets players with client mods enabled join.",
     "default": null
    },
    {
     "key": "bEnableBuildingPlayerUIdDisplay",
     "kind": "bool",
     "label": "Show builder ID on structures",
     "help": "Shows the player ID of whoever placed each structure.",
     "default": null
    },
    {
     "key": "PhysicsActiveDropItemMaxNum",
     "kind": "number",
     "label": "Max physics dropped items",
     "help": "Maximum dropped items that use physics.",
     "default": null
    },
    {
     "key": "bEnableFastTravelOnlyBaseCamp",
     "kind": "bool",
     "label": "Fast travel between bases only",
     "help": "Limits fast travel to moving between bases.",
     "default": null
    },
    {
     "key": "bAllowEnemyCampSpawnNearBaseCamp",
     "kind": "bool",
     "label": "Enemy camps near bases",
     "help": "Allows enemy camps to spawn near player bases.",
     "default": null
    },
    {
     "key": "bAllowEnhanceStat_Health",
     "kind": "bool",
     "label": "Stat points: HP",
     "help": "Allows putting stat points into HP.",
     "default": null
    },
    {
     "key": "bAllowEnhanceStat_Attack",
     "kind": "bool",
     "label": "Stat points: Attack",
     "help": "Allows putting stat points into Attack.",
     "default": null
    },
    {
     "key": "bAllowEnhanceStat_Stamina",
     "kind": "bool",
     "label": "Stat points: Stamina",
     "help": "Allows putting stat points into Stamina.",
     "default": null
    },
    {
     "key": "bAllowEnhanceStat_Weight",
     "kind": "bool",
     "label": "Stat points: Carry weight",
     "help": "Allows putting stat points into Carry Weight.",
     "default": null
    },
    {
     "key": "bAllowEnhanceStat_WorkSpeed",
     "kind": "bool",
     "label": "Stat points: Work speed",
     "help": "Allows putting stat points into Work Speed.",
     "default": null
    },
    {
     "key": "FishingDifficultyRate",
     "kind": "number",
     "label": "Fishing difficulty",
     "help": "Fishing difficulty, 0.1 to 1.0.",
     "default": null,
     "min": 0.1,
     "max": 1.0,
     "step": 0.1
    },
    {
     "key": "MonsterFarmActionSpeedRate",
     "kind": "number",
     "label": "Ranch production speed",
     "help": "Item production speed multiplier for Pals at the Ranch.",
     "default": null
    },
    {
     "key": "GuildRejoinCooldownMinutes",
     "kind": "number",
     "label": "Guild rejoin cooldown (min)",
     "help": "Minutes a player must wait before rejoining a guild.",
     "default": null
    },
    {
     "key": "BlockRespawnTime",
     "kind": "number",
     "label": "Respawn cooldown (s)",
     "help": "Cooldown before respawning after death, in seconds.",
     "default": null
    },
    {
     "key": "RespawnPenaltyDurationThreshold",
     "kind": "number",
     "label": "Respawn penalty threshold (s)",
     "help": "Survival time in seconds that triggers the respawn penalty multiplier on a later death.",
     "default": null
    },
    {
     "key": "RespawnPenaltyTimeScale",
     "kind": "number",
     "label": "Respawn penalty multiplier",
     "help": "Multiplier applied to the respawn cooldown.",
     "default": null
    },
    {
     "key": "DenyTechnologyList",
     "kind": "raw",
     "label": "Disabled technologies",
     "help": "Technology IDs to disable, in the game's list format, e.g. (\"PALBOX\",\"RepairBench\").",
     "default": null
    },
    {
     "key": "bDisplayPvPItemNumOnWorldMap_BaseCamp",
     "kind": "bool",
     "label": "PvP: show base item counts on map",
     "help": "Shows PvP-exclusive item counts per base on the map.",
     "default": null
    },
    {
     "key": "bDisplayPvPItemNumOnWorldMap_Player",
     "kind": "bool",
     "label": "PvP: show players on map",
     "help": "Shows player locations and PvP-exclusive item counts on the map.",
     "default": null
    },
    {
     "key": "bAdditionalDropItemWhenPlayerKillingInPvPMode",
     "kind": "bool",
     "label": "PvP: extra drop on kill",
     "help": "Drops a special item when a player is killed in PvP.",
     "default": null
    },
    {
     "key": "AdditionalDropItemWhenPlayerKillingInPvPMode",
     "kind": "text",
     "label": "PvP: extra drop item ID",
     "help": "Item ID dropped on PvP kills (when enabled).",
     "default": null
    },
    {
     "key": "AdditionalDropItemNumWhenPlayerKillingInPvPMode",
     "kind": "number",
     "label": "PvP: extra drop quantity",
     "help": "Quantity of the extra PvP item.",
     "default": null
    }
   ]
  }
 ]
};
